// frontend/src/components/notifications/UserPreferencesPanel.jsx
/**
 * UserPreferencesPanel — per-domain × per-channel notification routing matrix
 * + quiet-hours setter for push and email.
 *
 * @description
 *   Lets the user mute specific (domain, channel) combinations and set quiet
 *   hours for push/email. State is loaded from `/api/notify/preferences` and
 *   replaced atomically on save. Only active for the current user.
 *
 * @useWhen
 *   - Mounted on the user settings page (/settings/notifications)
 *
 * @dontUseFor
 *   - Per-trigger granular toggling (admin uses /api/admin/notify/triggers for that)
 *
 * @related
 *   - NotificationCenter — full notification history page
 *   - Bell — top-nav bell that obeys these prefs
 *
 * @props
 *   None — self-contained.
 */
import { useState, useEffect } from 'react'
import axios from 'axios'
import { Box, Stack, Typography, Switch, TextField, Button, Divider } from '@mui/material'
import { FS, SP, PANEL_SX } from '../../theme/tokens'
import { toast } from 'sonner'

const API = process.env.REACT_APP_API_BASE_URL

const DOMAINS = ['rma', 'pricing', 'sync', 'ops', 'po', 'scraper']
const INTENTS = ['action', 'data', 'sla', 'response', 'info']
const CHANNELS = ['in_app', 'push', 'email']

function key(intent, domain, channel) {
  return `${intent}:${domain}:${channel}`
}

export default function UserPreferencesPanel() {
  const [prefs, setPrefs] = useState({})
  const [pushQuietStart, setPushQuietStart] = useState('')
  const [pushQuietEnd, setPushQuietEnd] = useState('')
  const [emailQuietStart, setEmailQuietStart] = useState('')
  const [emailQuietEnd, setEmailQuietEnd] = useState('')
  const [saving, setSaving] = useState(false)
  const [mutes, setMutes] = useState([])

  useEffect(() => {
    axios.get(`${API}/api/notify/preferences`).then(r => {
      const map = {}
      ;(r.data.preferences || []).forEach(p => {
        map[key(p.intent, p.domain, p.channel)] = p
        if (p.channel === 'push' && p.quiet_start_hour != null) {
          setPushQuietStart(String(p.quiet_start_hour))
          setPushQuietEnd(String(p.quiet_end_hour ?? ''))
        }
        if (p.channel === 'email' && p.quiet_start_hour != null) {
          setEmailQuietStart(String(p.quiet_start_hour))
          setEmailQuietEnd(String(p.quiet_end_hour ?? ''))
        }
      })
      setPrefs(map)
    }).catch(() => {})
  }, [])

  useEffect(() => {
    axios.get(`${API}/api/notify/mutes`).then(r => setMutes(r.data.mutes || [])).catch(() => {})
  }, [])

  const isEnabled = (intent, domain, channel) => {
    const p = prefs[key(intent, domain, channel)]
    return p ? p.enabled : true
  }

  const toggle = (intent, domain, channel, value) => {
    setPrefs(prev => ({
      ...prev,
      [key(intent, domain, channel)]: { intent, domain, channel, enabled: value },
    }))
  }

  const unmute = async (id) => {
    try {
      await axios.delete(`${API}/api/notify/mutes/${id}`)
      setMutes(mutes.filter(m => m.id !== id))
      toast.success('Unmuted')
    } catch (e) {
      toast.error('Unmute failed')
    }
  }

  const save = async () => {
    setSaving(true)
    const list = Object.values(prefs)
    const pushQS = pushQuietStart === '' ? null : parseInt(pushQuietStart, 10)
    const pushQE = pushQuietEnd === '' ? null : parseInt(pushQuietEnd, 10)
    const emailQS = emailQuietStart === '' ? null : parseInt(emailQuietStart, 10)
    const emailQE = emailQuietEnd === '' ? null : parseInt(emailQuietEnd, 10)
    list.forEach(p => {
      if (p.channel === 'push') { p.quiet_start_hour = pushQS; p.quiet_end_hour = pushQE }
      if (p.channel === 'email') { p.quiet_start_hour = emailQS; p.quiet_end_hour = emailQE }
    })
    try {
      await axios.put(`${API}/api/notify/preferences`, { preferences: list })
      toast.success('Preferences saved')
    } catch (e) {
      toast.error('Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Box sx={PANEL_SX}>
      <Box sx={{ p: SP.section }}>
        <Typography sx={{ fontSize: FS.md, fontWeight: 600 }}>Channel preferences</Typography>
        <Typography sx={{ fontSize: FS.xs, color: 'text.secondary', mt: 0.5 }}>
          Toggle channels per (domain × intent). Defaults are on. Changes save when you click Save.
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ p: SP.section, overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: 8 }}>Domain</th>
              <th style={{ textAlign: 'left', padding: 8 }}>Intent</th>
              {CHANNELS.map(c => <th key={c} style={{ padding: 8 }}>{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {DOMAINS.flatMap(d => INTENTS.map(i => (
              <tr key={`${d}:${i}`}>
                <td style={{ padding: 8 }}>{d}</td>
                <td style={{ padding: 8 }}>{i}</td>
                {CHANNELS.map(c => (
                  <td key={c} style={{ padding: 8, textAlign: 'center' }}>
                    <Switch
                      size="small"
                      checked={isEnabled(i, d, c)}
                      onChange={(e) => toggle(i, d, c, e.target.checked)}
                    />
                  </td>
                ))}
              </tr>
            )))}
          </tbody>
        </table>
      </Box>
      <Divider />
      <Box sx={{ p: SP.section }}>
        <Typography sx={{ fontSize: FS.md, fontWeight: 600, mb: SP.sm }}>Quiet hours</Typography>
        <Typography sx={{ fontSize: FS.xs, color: 'text.secondary', mb: SP.sm }}>
          During these hours, push and email notifications are suppressed. Use 24h format. Leave blank to disable quiet hours.
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: SP.sm }}>
          <Typography sx={{ minWidth: 60, fontSize: FS.sm }}>Push:</Typography>
          <TextField size="small" label="Start" placeholder="22" type="number" value={pushQuietStart} onChange={e => setPushQuietStart(e.target.value)} sx={{ width: 100 }} />
          <TextField size="small" label="End" placeholder="7" type="number" value={pushQuietEnd} onChange={e => setPushQuietEnd(e.target.value)} sx={{ width: 100 }} />
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography sx={{ minWidth: 60, fontSize: FS.sm }}>Email:</Typography>
          <TextField size="small" label="Start" placeholder="0" type="number" value={emailQuietStart} onChange={e => setEmailQuietStart(e.target.value)} sx={{ width: 100 }} />
          <TextField size="small" label="End" placeholder="6" type="number" value={emailQuietEnd} onChange={e => setEmailQuietEnd(e.target.value)} sx={{ width: 100 }} />
        </Stack>
      </Box>
      <Divider />
      <Box sx={{ p: SP.section }}>
        <Typography sx={{ fontSize: FS.md, fontWeight: 600, mb: SP.sm }}>Active mutes</Typography>
        <Typography sx={{ fontSize: FS.xs, color: 'text.secondary', mb: SP.sm }}>
          These specific notifications or triggers are temporarily/permanently silenced.
        </Typography>
        {mutes.length === 0 ? (
          <Typography sx={{ fontSize: FS.xs, color: 'text.disabled' }}>No active mutes.</Typography>
        ) : (
          mutes.map(m => (
            <Stack key={m.id} direction="row" alignItems="center" sx={{ py: 0.5, borderBottom: '1px solid', borderBottomColor: 'divider' }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography sx={{ fontSize: FS.xs, fontWeight: 500 }}>
                  {m.dedup_key ? `incident: ${m.dedup_key}` : `trigger: ${m.trigger_key}`}
                </Typography>
                <Typography sx={{ fontSize: FS.xxs, color: 'text.secondary' }}>
                  until {m.muted_until ? new Date(m.muted_until).toLocaleString('de-DE') : 'forever'} {m.reason ? `· ${m.reason}` : ''}
                </Typography>
              </Box>
              <Button size="small" onClick={() => unmute(m.id)} sx={{ fontSize: FS.xxs }}>Unmute</Button>
            </Stack>
          ))
        )}
      </Box>
      <Divider />
      <Box sx={{ p: SP.section, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save preferences'}
        </Button>
      </Box>
    </Box>
  )
}
